import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { xor } from 'lodash';
import { IconButton } from '@ui/button';
import { ResponsiveImage } from '@web/molecules/ResponsiveImage';
import { ButtonLink } from '@web/atoms';
import {
  IngredientModal,
  useResolvedIngredientModal,
  ProductSelector,
  MealPrepModal,
} from '@web/components';
import {
  userViewMealPlanTypeScreen,
  userViewPlanDetailsPage,
} from '@web/account/MealPrepTypeSelectionForm/events';
import { CMSBlok } from '../../cms';
import { CheckoutBar } from '../CheckoutBar';
import { useReporter, useElevarReporter } from '../../reporter';
import { useShopifyCartContext } from '../OnboardingFlowContainer/ShopifyCartProvider';
import { buildSelectedElevarMealPrepProduct } from '../OnboardingFlowContainer/elevarHelpers';
import { useSessionCustomer } from '../../customer';
import {
  userContinuesFromRecommendedPlan,
  recipeSelected,
  recipeUnselected,
  userExpandsFreshRecipeDetails,
  userExpandsMealPlanDetails,
  mealPlanTypeSelected,
} from './events.js';
import {
  StyledRecommendedPlanContainer,
  StyledForm,
  HeaderContainer,
  FullHeadline,
  FullSubheadline,
  ProductSelectorContainerDesktop,
  ProductSelectorContainerMobile,
  ProductCardContainer,
  PlanContainer,
  SectionHeader,
  DesktopImageContainer,
  ContentContainer,
  ButtonContainer,
} from './styles';

export const OptimizedMealPlanForm = ({
  onSubmit,
  headline,
  subheadline,
  modals,
  ctaText,
  freshRecipes,
  defaultFreshRecipes,
  bakedRecipes,
  defaultBakedRecipes,
  defaultMealPrepType,
  mealPrepTypes,
  unavailableMealPrepTypes,
  collapsedMealPrepTypes,
  onBackButtonClick,
  unavailableFreshRecipes,
  unavailableBakedRecipes,
  period,
  getConfigFromRecipes,
  body,
  modalContent,
  notSelectableText,
  ctaButtonVariant = 'primary',
  isWithinOptimized = false,
  desktopImage,
  recipeModalsList,
}) => {
  const availableModalContent = modalContent.filter(
    ({ value }) => !unavailableMealPrepTypes?.includes(value),
  );

  const [selectedMealPrepType, setSelectedMealPrepType] =
    useState(defaultMealPrepType);
  const [selectedFreshRecipes, setSelectedFreshRecipes] =
    useState(defaultFreshRecipes);
  const [selectedBakedRecipes, setSelectedBakedRecipes] =
    useState(defaultBakedRecipes);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const viewEventsFired = useRef(false);
  const elevarViewItemsTracked = useRef(false);

  const reporter = useReporter();
  const elevarReporter = useElevarReporter();

  const { customer, isAppSession } = useSessionCustomer();
  const { cart } = useShopifyCartContext();
  const customerEmail = cart?.customer?.email || customer?.email;
  const elevarUserProperties = useMemo(() => {
    const props = {};
    if (customerEmail) props.customer_email = customerEmail;
    if (customer?.id) props.customer_id = customer.id;
    return props;
  }, [customerEmail, customer?.id]);
  const allMealPrepProducts = useMemo(
    () => [...(mealPrepTypes || []), ...(collapsedMealPrepTypes || [])],
    [mealPrepTypes, collapsedMealPrepTypes],
  );

  const { modal, onRecipeDetailsClick } = useResolvedIngredientModal(
    modals,
    recipeModalsList,
  );

  useEffect(() => {
    if (!viewEventsFired.current) {
      if (isWithinOptimized) {
        reporter.tag(userViewPlanDetailsPage());
      } else {
        reporter.tag(userViewMealPlanTypeScreen());
      }
      viewEventsFired.current = true;
    }
    if (
      selectedMealPrepType === 'fresh' ||
      selectedMealPrepType === 'half_fresh'
    ) {
      setSelectedBakedRecipes([]);
    } else {
      setSelectedBakedRecipes(defaultBakedRecipes);
    }
  }, [
    reporter,
    cart,
    selectedMealPrepType,
    defaultBakedRecipes,
    isWithinOptimized,
  ]);

  useEffect(() => {
    if (elevarViewItemsTracked.current || !mealPrepTypes?.length) return;
    const normalizedProducts = allMealPrepProducts
      .map(planType =>
        buildSelectedElevarMealPrepProduct({
          mealPrepProducts: allMealPrepProducts,
          mealPrepTypes,
          selectedMealPrepType: planType.value,
          selectedFreshRecipes,
          selectedBakedRecipes,
          getConfigFromRecipes,
        }),
      )
      .filter(Boolean);

    normalizedProducts.forEach(product => {
      elevarReporter?.elevarViewItem({
        planType: product,
        userProperties: elevarUserProperties,
      });
    });
    elevarViewItemsTracked.current = true;
  }, [
    allMealPrepProducts,
    elevarReporter,
    elevarUserProperties,
    selectedFreshRecipes,
    selectedBakedRecipes,
    getConfigFromRecipes,
    mealPrepTypes,
  ]);

  const onFreshChange = freshRecipes => {
    const currentRecipes = selectedFreshRecipes ?? [];
    const recipeAdded = freshRecipes.length > currentRecipes.length;
    const recipe = xor(freshRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedFreshRecipes(freshRecipes);
  };

  const onBakedChange = bakedRecipes => {
    const currentRecipes = selectedBakedRecipes ?? [];
    const recipeAdded = bakedRecipes.length > currentRecipes.length;
    const recipe = xor(bakedRecipes, currentRecipes)[0];
    const event = recipeAdded ? recipeSelected : recipeUnselected;
    reporter.tag(event(recipe));
    setSelectedBakedRecipes(bakedRecipes);
  };

  const onMealPrepChange = product => {
    reporter.tag(mealPlanTypeSelected(product[0]));
    if (unavailableMealPrepTypes?.includes(product[0])) return null;
    setSelectedMealPrepType(product[0]);
  };

  const isValid =
    selectedMealPrepType === 'fresh' || selectedMealPrepType === 'half_fresh'
      ? selectedFreshRecipes && selectedFreshRecipes.length > 0
      : selectedFreshRecipes &&
        selectedFreshRecipes.length > 0 &&
        selectedBakedRecipes &&
        selectedBakedRecipes.length > 0;

  const onSubmitHandler = async event => {
    event.preventDefault();
    if (!isValid) {
      return;
    }
    const email = cart?.customer?.email || customer?.email;
    reporter.tag(
      userContinuesFromRecommendedPlan(
        email,
        selectedMealPrepType,
        selectedFreshRecipes,
        selectedBakedRecipes,
      ),
    );

    const selectedProduct = buildSelectedElevarMealPrepProduct({
      mealPrepProducts: allMealPrepProducts,
      mealPrepTypes,
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
      getConfigFromRecipes,
    });
    if (selectedProduct) {
      elevarReporter?.elevarAddToCart({
        product: selectedProduct,
        userProperties: elevarUserProperties,
      });
    }

    setIsSubmitting(true);
    await onSubmit(
      selectedMealPrepType,
      selectedFreshRecipes,
      selectedBakedRecipes,
    );
  };

  const onDetailsClick = () => {
    reporter.tag(userExpandsMealPlanDetails());
    setShowModal(true);
  };

  const { price, discountedPrice, message } = getConfigFromRecipes(
    selectedMealPrepType,
    selectedFreshRecipes,
    selectedBakedRecipes,
  );

  const checkoutBarProps = {
    ctaText,
    isSubmitDisabled: isSubmitting,
    isBackButtonDisabled: isSubmitting,
    shown: true,
    period,
    isSubmitting: isSubmitting,
    relativePosition: isAppSession,
    price,
    discountedPrice,
    message,
    buttonVariant: ctaButtonVariant,
  };

  const onRecipeDetailsHandler = product => {
    reporter.tag(userExpandsFreshRecipeDetails(product.value));
    if (product?.title?.includes('Baked')) {
      onRecipeDetailsClick('baked_' + product.value);
    } else {
      onRecipeDetailsClick(product.value);
    }
  };

  const productSelectorProps = {
    onDetailsClick: onRecipeDetailsHandler,
    productCardVariant: 'recommended',
    direction: 'horizontal',
    period,
    buttonIcon: 'plus',
    gridColumns: 2,
    stackOnMobile: true,
    cardWidth: 310,
    cardWidthMobile: 360,
    productCardBackgroundColor: 'base',
  };

  const getConfiguredPlanTypes = planTypes =>
    planTypes?.map(planType => ({
      ...planType,
      shouldShowMoreDetails: false,
      inputType: 'radio',
    }));

  const mealPrepSelectorProps = {
    selected: [selectedMealPrepType],
    unavailableProducts: unavailableMealPrepTypes,
    variant: 'radio',
    direction: 'horizontal',
    cardOrientation: 'column',
    notSelectableText: notSelectableText,
    productCardBackgroundColor: 'base',
  };

  return (
    <StyledForm onSubmit={onSubmitHandler}>
      <StyledRecommendedPlanContainer>
        <ButtonContainer aria-live="polite">
          <IconButton
            aria-label="go to previous step"
            icon={{
              iconName: 'arrow-left',
              size: '24px',
              alt: 'back',
              id: 'prev_step_footer',
              color: 'hero.plum',
            }}
            onClick={onBackButtonClick}
            type="button"
            variant="lilac"
          />
        </ButtonContainer>
        <DesktopImageContainer>
          <ResponsiveImage
            priority={true}
            {...desktopImage}
            height={362}
            width={406}
          />
        </DesktopImageContainer>

        <MealPrepModal
          mealPrepTypes={availableModalContent}
          shown={showModal}
          onClickClose={() => setShowModal(false)}
        ></MealPrepModal>
        <ContentContainer>
          <HeaderContainer>
            <FullHeadline>{headline}</FullHeadline>
            <FullSubheadline>
              {subheadline}{' '}
              {mealPrepTypes.length > 1 && (
                <ButtonLink variant="text.link" onClick={onDetailsClick}>
                  See details
                </ButtonLink>
              )}
            </FullSubheadline>
            {mealPrepTypes.length === 1 ? (
              <ProductSelector
                gridColumns={1}
                mobileGridColumns={1}
                stackOnMobile={false}
                products={getConfiguredPlanTypes(mealPrepTypes)}
                unavailableProducts={unavailableMealPrepTypes}
                onChange={onMealPrepChange}
                productCardVariant="cro"
                showPricePerWeek={false}
                {...mealPrepSelectorProps}
                cardHeight={165}
              />
            ) : (
              <div>
                <ProductSelectorContainerDesktop>
                  <ProductSelector
                    gridColumns={2}
                    cardWidth={318}
                    stackOnMobile={false}
                    offsetBadge={true}
                    badgeVariant="success"
                    products={getConfiguredPlanTypes(mealPrepTypes)}
                    unavailableProducts={unavailableMealPrepTypes}
                    onChange={onMealPrepChange}
                    productCardVariant="recommended"
                    cardImgSize={72}
                    {...mealPrepSelectorProps}
                    cardOrientation="row"
                  />
                </ProductSelectorContainerDesktop>
                <ProductSelectorContainerMobile>
                  <ProductSelector
                    gridColumns={2}
                    cardWidth={176}
                    stackOnMobile={false}
                    offsetBadge={true}
                    badgeVariant="success"
                    products={getConfiguredPlanTypes(mealPrepTypes)}
                    unavailableProducts={unavailableMealPrepTypes}
                    onChange={onMealPrepChange}
                    productCardVariant="recommended"
                    alignColumnContent="center"
                    cardImgSize={72}
                    {...mealPrepSelectorProps}
                  />
                </ProductSelectorContainerMobile>
              </div>
            )}
          </HeaderContainer>
          <ProductCardContainer
            paddingBottom={Boolean(collapsedMealPrepTypes.length <= 0)}
          >
            <SectionHeader>Choose up to 4 recipes</SectionHeader>
            <fieldset>
              {selectedMealPrepType === 'fresh' ||
              selectedMealPrepType === 'half_fresh' ? (
                <>
                  <ProductSelector
                    products={freshRecipes}
                    selected={selectedFreshRecipes}
                    unavailableProducts={unavailableFreshRecipes}
                    {...productSelectorProps}
                    onChange={onFreshChange}
                  />
                </>
              ) : (
                <>
                  <ProductSelector
                    products={freshRecipes}
                    selected={selectedFreshRecipes}
                    unavailableProducts={unavailableFreshRecipes}
                    {...productSelectorProps}
                    onChange={onFreshChange}
                  />
                  <SectionHeader>Choose up to 2 baked recipes</SectionHeader>
                  <ProductSelector
                    products={bakedRecipes}
                    selected={selectedBakedRecipes}
                    unavailableProducts={unavailableBakedRecipes}
                    {...productSelectorProps}
                    onChange={onBakedChange}
                  />
                </>
              )}
            </fieldset>

            <IngredientModal {...modal} />
          </ProductCardContainer>
          {collapsedMealPrepTypes.length > 0 && (
            <PlanContainer>
              <SectionHeader>Looking to try us out for less?</SectionHeader>
              <ProductSelector
                gridColumns={collapsedMealPrepTypes.length}
                products={getConfiguredPlanTypes(collapsedMealPrepTypes)}
                unavailableProducts={unavailableMealPrepTypes}
                variant="radio"
                productCardVariant="cro"
                direction="horizontal"
                onChange={onMealPrepChange}
                {...mealPrepSelectorProps}
              />
            </PlanContainer>
          )}
        </ContentContainer>
      </StyledRecommendedPlanContainer>

      {body?.map(content => (
        <CMSBlok key={content._uid} content={content} />
      ))}
      <CheckoutBar {...checkoutBarProps} />
    </StyledForm>
  );
};
OptimizedMealPlanForm.displayName = 'OptimizedMealPlanForm';
OptimizedMealPlanForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  headline: PropTypes.string,
  subheadline: PropTypes.string,
  modals: PropTypes.array,
  ctaText: PropTypes.string.isRequired,
  freshRecipes: PropTypes.array,
  defaultFreshRecipes: PropTypes.array,
  bakedRecipes: PropTypes.array,
  defaultBakedRecipes: PropTypes.array,
  mealPrepTypes: PropTypes.array,
  defaultMealPrepTypes: PropTypes.array,
  onBackButtonClick: PropTypes.func.isRequired,
  unavailableFreshRecipes: PropTypes.array,
  unavailableBakedRecipes: PropTypes.array,
  unavailableMealPrepTypes: PropTypes.array,
  collapsedMealPrepTypes: PropTypes.array,
  period: PropTypes.string.isRequired,
  headerImage: PropTypes.object,
  body: PropTypes.array,
  modelContent: PropTypes.array,
  getConfigFromRecipes: PropTypes.func.isRequired,
  notSelectableText: PropTypes.string,
  hideHalfFresh: PropTypes.bool,
  showFreshOnly: PropTypes.bool,
  hideMixed: PropTypes.bool,
  recipeModalsList: PropTypes.array,
};
